import { SignedIn, SignedOut } from '@clerk/clerk-react';
import * as Sentry from '@sentry/react';
import { Suspense, useEffect, useState } from 'react';
import { apiClient } from './api';
import { ConflictModal } from './components/conflict-modal';
import { DoctorAccessGate } from './components/DoctorAccessGate';
import { Dashboard } from './components/dashboard';
import { FeatureError } from './components/FeatureError';
import { Header } from './components/header';
import { StagingGate } from './components/StagingGate';
import { SessionIdleGuard } from './components/session-idle-guard';
import { Sidebar } from './components/sidebar';
import { LoadingSpinner } from './components/ui/loading-spinner';
import { Toaster } from './components/ui/sonner';
import { useAuth } from './contexts/auth-context';
import { trackPageView, trackStripeConnectComplete } from './utils/analytics';
// Landing page lazy-loaded (only needed for signed-out users)
import { lazyRetry } from './utils/lazyRetry';

const Landing = lazyRetry(() =>
  import('./components/landing/Landing').then((m) => ({ default: m.Landing })),
);

// Enhanced lazy-loaded components with improved retry and chunk splitting
const AttorneyCaseDetail = lazyRetry(() =>
  import('./components/AttorneyCaseDetail').then((m) => ({ default: m.AttorneyCaseDetail })),
);
const AttorneyIntake = lazyRetry(() =>
  import('./components/attorney-intake').then((m) => ({ default: m.AttorneyIntake })),
);
const AttorneyClientIntake = lazyRetry(() =>
  import('./components/attorney-client-intake').then((m) => ({ default: m.AttorneyClientIntake })),
);

// Pre-screening requests with modular loading
const PreScreeningRequests = lazyRetry(
  () =>
    import('./components/pre-screening-requests').then((m) => ({
      default: m.PreScreeningRequests,
    })),
  { maxRetries: 3, retryDelay: 1000, cacheBypass: true },
);

const CasesAndRecords = lazyRetry(() =>
  import('./components/cases-and-records').then((m) => ({ default: m.CasesAndRecords })),
);
const CaseRecordDetail = lazyRetry(() =>
  import('./components/case-record-detail').then((m) => ({ default: m.CaseRecordDetail })),
);
const Invoices = lazyRetry(() =>
  import('./components/invoices').then((m) => ({ default: m.Invoices })),
);
const Billing = lazyRetry(() =>
  import('./components/billing').then((m) => ({ default: m.Billing })),
);
const Settings = lazyRetry(() =>
  import('./components/settings').then((m) => ({ default: m.Settings })),
);
const AdminDiscountManagement = lazyRetry(() =>
  import('./components/admin-discount-management').then((m) => ({
    default: m.AdminDiscountManagement,
  })),
);
const SystemStatus = lazyRetry(() => import('./components/system-status'));
const AppointmentScheduling = lazyRetry(() =>
  import('./components/appointment-scheduling').then((m) => ({ default: m.AppointmentScheduling })),
);
const DoctorAppointment = lazyRetry(() =>
  import('./components/doctor-appointment').then((m) => ({ default: m.DoctorAppointment })),
);
const AttorneyDashboard = lazyRetry(() =>
  import('./components/AttorneyDashboard').then((m) => ({ default: m.AttorneyDashboard })),
);
const AttorneyLayout = lazyRetry(() =>
  import('./components/AttorneyLayout').then((m) => ({ default: m.AttorneyLayout })),
);
const TermsAndConditions = lazyRetry(() =>
  import('./components/legal/TermsAndConditions').then((m) => ({ default: m.TermsAndConditions })),
);
const PrivacyPolicy = lazyRetry(() =>
  import('./components/legal/PrivacyPolicy').then((m) => ({ default: m.PrivacyPolicy })),
);

// SEO Landing Pages
const PhysiciansPage = lazyRetry(() =>
  import('./components/landing/PhysiciansPage').then((m) => ({ default: m.PhysiciansPage })),
);
const ComparePage = lazyRetry(() =>
  import('./components/landing/ComparePage').then((m) => ({ default: m.ComparePage })),
);
const SideIncomePage = lazyRetry(() =>
  import('./components/landing/SideIncomePage').then((m) => ({ default: m.SideIncomePage })),
);
const BlogPage = lazyRetry(() =>
  import('./components/landing/BlogPage').then((m) => ({ default: m.BlogPage })),
);
const HowItWorksPage = lazyRetry(() =>
  import('./components/landing/HowItWorksPage').then((m) => ({ default: m.HowItWorksPage })),
);
const PricingPage = lazyRetry(() =>
  import('./components/landing/PricingPage').then((m) => ({ default: m.PricingPage })),
);
const SecurityPage = lazyRetry(() =>
  import('./components/landing/SecurityPage').then((m) => ({ default: m.SecurityPage })),
);
const AttorneysPage = lazyRetry(() =>
  import('./components/landing/AttorneysPage').then((m) => ({ default: m.AttorneysPage })),
);
const Auth = lazyRetry(() => import('./components/auth').then((m) => ({ default: m.Auth })));

export default function App() {
  const [currentView, setCurrentView] = useState('dashboard');
  const [selectedCaseId, setSelectedCaseId] = useState<string | null>(null);
  const [selectedCaseRecordId, setSelectedCaseRecordId] = useState<string | null>(null);
  const [isConflictModalOpen, setIsConflictModalOpen] = useState(false);
  const [showIntakeView, setShowIntakeView] = useState(false);
  const [showSystemStatus, setShowSystemStatus] = useState(false);
  const [vanitySlug, setVanitySlug] = useState<string | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [pendingRequestId, setPendingRequestId] = useState<string | null>(null);
  const [pendingInvoiceId, setPendingInvoiceId] = useState<string | null>(null);

  // Appointment scheduling state
  const [showAppointmentScheduling, setShowAppointmentScheduling] = useState(false);
  const [showDoctorAppointment, setShowDoctorAppointment] = useState(false);
  const [appointmentId, setAppointmentId] = useState<string | null>(null);
  const [appointmentToken, setAppointmentToken] = useState<string | null>(null);

  // Attorney dashboard state
  const [showAttorneyDashboard, setShowAttorneyDashboard] = useState(false);
  const [attorneyToken, setAttorneyToken] = useState<string | null>(null);

  // Attorney client intake state
  const [showAttorneyClientIntake, setShowAttorneyClientIntake] = useState(false);

  // Legal pages state
  const [showTermsPage, setShowTermsPage] = useState(false);
  const [showPrivacyPage, setShowPrivacyPage] = useState(false);

  // SEO landing pages state
  const [showPhysiciansPage, setShowPhysiciansPage] = useState(false);
  const [showComparePage, setShowComparePage] = useState(false);
  const [showSideIncomePage, setShowSideIncomePage] = useState(false);
  const [showBlogPage, setShowBlogPage] = useState(false);
  const [showHowItWorksPage, setShowHowItWorksPage] = useState(false);
  const [showPricingPage, setShowPricingPage] = useState(false);
  const [showSecurityPage, setShowSecurityPage] = useState(false);
  const [showAttorneysPage, setShowAttorneysPage] = useState(false);

  // Team member sign-up state (from invite link)
  const [showTeamSignUp, setShowTeamSignUp] = useState(false);
  const [teamInviteId, setTeamInviteId] = useState<string | null>(null);
  const [teamInviteEmail, setTeamInviteEmail] = useState<string | undefined>();
  const [teamInviteDoctorName, setTeamInviteDoctorName] = useState<string | undefined>();

  // Auth page state (for /auth route when signed out, or ?ref= links)
  const [showAuthPage, setShowAuthPage] = useState(false);
  const [authDefaultToSignUp, setAuthDefaultToSignUp] = useState(false);

  // Route initialization flag — prevents authenticated view tracking from
  // firing before the initial route useEffect has committed its state updates
  const [routeInitialized, setRouteInitialized] = useState(false);

  // Staging gate state
  const [stagingProtectionEnabled, setStagingProtectionEnabled] = useState(false);
  const [stagingAccessGranted, setStagingAccessGranted] = useState(
    () => sessionStorage.getItem('staging_access_granted') === 'true',
  );
  const [stagingCheckDone, setStagingCheckDone] = useState(false);

  // Check for special routes on app load - do this BEFORE auth check
  useEffect(() => {
    const pathname = window.location.pathname;
    const pathSegments = pathname.split('/').filter(Boolean);
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');

    // Persist referral code in sessionStorage so it survives Clerk auth redirects
    const refCode = urlParams.get('ref');
    if (refCode) {
      const trimmedRef = refCode.trim().toUpperCase().slice(0, 30);
      if (trimmedRef && /^[A-Z0-9-]+$/.test(trimmedRef)) {
        sessionStorage.setItem('referral_code', trimmedRef);
      }
    }

    // Show auth/signup page for /auth route or ?ref= links
    if (pathname === '/auth') {
      setShowAuthPage(true);
      if (refCode?.trim()) setAuthDefaultToSignUp(true);
      trackPageView('/auth');
      setRouteInitialized(true);
      return;
    }
    // Referral links (/?ref=CODE) → show signup page so user can register
    if (refCode && (pathname === '/' || pathname === '')) {
      setShowAuthPage(true);
      setAuthDefaultToSignUp(true);
      trackPageView('/auth');
      setRouteInitialized(true);
      return;
    }

    // Check if this is the system status page
    if (
      pathname === '/system-status' ||
      (pathSegments.length === 1 && pathSegments[0] === 'system-status')
    ) {
      setShowSystemStatus(true);
      trackPageView('/system-status');
      return;
    }

    // Check for legal pages: /terms and /privacy
    if (pathname === '/terms' || (pathSegments.length === 1 && pathSegments[0] === 'terms')) {
      setShowTermsPage(true);
      trackPageView('/terms');
      return;
    }
    if (pathname === '/privacy' || (pathSegments.length === 1 && pathSegments[0] === 'privacy')) {
      setShowPrivacyPage(true);
      trackPageView('/privacy');
      return;
    }

    // Check for appointment scheduling route: /appointments/:id/schedule?token=xxx
    if (
      pathSegments.length === 3 &&
      pathSegments[0] === 'appointments' &&
      pathSegments[2] === 'schedule'
    ) {
      const apptId = pathSegments[1];
      if (token && apptId) {
        setAppointmentId(apptId);
        setAppointmentToken(token);
        setShowAppointmentScheduling(true);
        trackPageView('/appointments/schedule');
        return;
      }
    }

    // Check for doctor appointment route: /appointments/:id/doctor?token=xxx
    if (
      pathSegments.length === 3 &&
      pathSegments[0] === 'appointments' &&
      pathSegments[2] === 'doctor'
    ) {
      const apptId = pathSegments[1];
      if (token && apptId) {
        setAppointmentId(apptId);
        setAppointmentToken(token);
        setShowDoctorAppointment(true);
        trackPageView('/appointments/doctor');
        return;
      }
    }

    // Check for attorney dashboard route: /attorney/dashboard?token=xxx
    // With token → JWT magic link mode; without token → Clerk auth (handled by <SignedIn>)
    if (
      pathSegments.length === 2 &&
      pathSegments[0] === 'attorney' &&
      pathSegments[1] === 'dashboard'
    ) {
      if (token) {
        setAttorneyToken(token);
        setShowAttorneyDashboard(true);
        trackPageView('/attorney/dashboard');
        return;
      }
    }

    // Check for SEO landing pages
    if (
      pathname === '/physicians' ||
      (pathSegments.length === 1 && pathSegments[0] === 'physicians')
    ) {
      setShowPhysiciansPage(true);
      trackPageView('/physicians');
      return;
    }
    if (pathname === '/compare' || (pathSegments.length === 1 && pathSegments[0] === 'compare')) {
      setShowComparePage(true);
      trackPageView('/compare');
      return;
    }
    if (
      pathname === '/side-income' ||
      (pathSegments.length === 1 && pathSegments[0] === 'side-income')
    ) {
      setShowSideIncomePage(true);
      trackPageView('/side-income');
      return;
    }
    if (pathname === '/blog' || (pathSegments.length === 1 && pathSegments[0] === 'blog')) {
      setShowBlogPage(true);
      trackPageView('/blog');
      return;
    }
    if (
      pathname === '/how-it-works' ||
      (pathSegments.length === 1 && pathSegments[0] === 'how-it-works')
    ) {
      setShowHowItWorksPage(true);
      trackPageView('/how-it-works');
      return;
    }
    if (pathname === '/pricing' || (pathSegments.length === 1 && pathSegments[0] === 'pricing')) {
      setShowPricingPage(true);
      trackPageView('/pricing');
      return;
    }
    if (pathname === '/security' || (pathSegments.length === 1 && pathSegments[0] === 'security')) {
      setShowSecurityPage(true);
      trackPageView('/security');
      return;
    }
    if (
      pathname === '/attorneys' ||
      (pathSegments.length === 1 && pathSegments[0] === 'attorneys')
    ) {
      setShowAttorneysPage(true);
      trackPageView('/attorneys');
      return;
    }

    // Check for team member sign-up route: /sign-up?role=TEAM_MEMBER&invite=<id>
    if (pathname === '/sign-up' || (pathSegments.length === 1 && pathSegments[0] === 'sign-up')) {
      const roleParam = urlParams.get('role');
      const inviteParam = urlParams.get('invite');
      // Check URL params first, then sessionStorage (survives page refresh)
      const effectiveInviteId =
        roleParam === 'TEAM_MEMBER' && inviteParam
          ? inviteParam
          : sessionStorage.getItem('team_invite_id');
      if (effectiveInviteId) {
        if (inviteParam) {
          sessionStorage.setItem('team_invite_id', effectiveInviteId);
        }
        setTeamInviteId(effectiveInviteId);
        setShowTeamSignUp(true);
        // Fetch invite details to pre-populate the form
        const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3005/api/v1';
        const cachedEmail = sessionStorage.getItem('team_invite_email');
        const cachedDoctor = sessionStorage.getItem('team_invite_doctor');
        if (cachedEmail) setTeamInviteEmail(cachedEmail);
        if (cachedDoctor) setTeamInviteDoctorName(cachedDoctor);
        fetch(`${apiUrl}/team/invite-details/${encodeURIComponent(effectiveInviteId)}`)
          .then((res) => (res.ok ? res.json() : null))
          .then((data) => {
            if (data?.data) {
              setTeamInviteEmail(data.data.email);
              setTeamInviteDoctorName(data.data.doctorName);
              sessionStorage.setItem('team_invite_email', data.data.email);
              sessionStorage.setItem('team_invite_doctor', data.data.doctorName);
            }
          })
          .catch(() => {});
        window.history.replaceState({}, '', '/sign-up');
        trackPageView('/sign-up');
        return;
      }
    }

    // Check if this is a /client-intake/:vanityUrl path (attorney client intake)
    if (pathSegments.length === 2 && pathSegments[0] === 'client-intake') {
      const slug = pathSegments[1];
      if (/^[a-zA-Z0-9-]+$/i.test(slug)) {
        setVanitySlug(slug);
        setShowAttorneyClientIntake(true);
        trackPageView(`/client-intake/${slug}`);
        return;
      }
    }

    // Check if this is an /intake/:vanityUrl path
    if (pathSegments.length === 2 && pathSegments[0] === 'intake') {
      const slug = pathSegments[1];
      // Validate that this is a proper vanity URL format (alphanumeric + hyphens, case-insensitive)
      if (/^[a-zA-Z0-9-]+$/i.test(slug)) {
        setVanitySlug(slug);
        setShowIntakeView(true);
        trackPageView(`/intake/${slug}`);
      }
    }
    // Also check if this is a single segment vanity URL (e.g., /dr-greenfield)
    // Skip reserved paths that are used by pushState virtual routing
    else if (pathSegments.length === 1 && !pathSegments[0].startsWith('intake')) {
      const slug = pathSegments[0];
      const RESERVED_PATHS = new Set([
        'auth',
        'dashboard',
        'requests',
        'cases',
        'invoices',
        'billing',
        'settings',
        'admin',
        'system-status',
        'terms',
        'privacy',
        'physicians',
        'compare',
        'side-income',
        'blog',
        'how-it-works',
        'pricing',
        'security',
        'attorneys',
        'appointments',
        'attorney',
        'intake',
        'client-intake',
        'intake-requests',
        'sign-up',
      ]);
      // Validate that this is a proper vanity URL format and not a reserved path
      if (/^[a-zA-Z0-9-]+$/i.test(slug) && !RESERVED_PATHS.has(slug.toLowerCase())) {
        setVanitySlug(slug);
        setShowIntakeView(true);
        trackPageView(`/intake/${slug}`);
      }
    }

    setRouteInitialized(true);
  }, []);

  const { isAuthenticated, isLoading, user } = useAuth();

  // Check for Stripe return on app load — route to the page that initiated onboarding
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('success') === 'stripe') {
      trackStripeConnectComplete();
      const targetView = window.location.pathname === '/billing' ? 'billing' : 'settings';
      setCurrentView(targetView);
    } else if (params.get('refresh') === 'stripe') {
      const targetView = window.location.pathname === '/billing' ? 'billing' : 'settings';
      setCurrentView(targetView);
    }
  }, []);

  // Track authenticated view changes as virtual pageviews
  const VIEW_PATH_MAP: Record<string, string> = {
    dashboard: '/dashboard',
    requests: '/requests',
    cases: '/cases',
    'case-detail': '/cases/detail',
    'case-record-detail': '/cases/record',
    invoices: '/invoices',
    billing: '/billing',
    settings: '/settings',
    admin: '/admin',
  };

  // Don't push authenticated view paths when a public/special page is active
  const isSpecialPage =
    showIntakeView ||
    showAttorneyClientIntake ||
    !!vanitySlug ||
    showSystemStatus ||
    showTermsPage ||
    showPrivacyPage ||
    showPhysiciansPage ||
    showComparePage ||
    showSideIncomePage ||
    showBlogPage ||
    showHowItWorksPage ||
    showPricingPage ||
    showSecurityPage ||
    showAttorneysPage ||
    showAppointmentScheduling ||
    showDoctorAppointment ||
    showAttorneyDashboard ||
    showTeamSignUp ||
    showAuthPage;

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional dep list; loader fn identity changes every render (pre-existing pattern)
  useEffect(() => {
    if (!routeInitialized || !isAuthenticated || isSpecialPage) return;

    const basePath = VIEW_PATH_MAP[currentView];
    if (!basePath) return;

    let pagePath = basePath;
    if (currentView === 'case-detail' && selectedCaseId) {
      pagePath = `${basePath}/${selectedCaseId}`;
    } else if (currentView === 'case-record-detail' && selectedCaseRecordId) {
      pagePath = `${basePath}/${selectedCaseRecordId}`;
    }

    trackPageView(pagePath);
  }, [
    currentView,
    selectedCaseId,
    selectedCaseRecordId,
    isAuthenticated,
    isSpecialPage,
    routeInitialized,
  ]);

  // Handle browser back/forward navigation
  useEffect(() => {
    const PATH_VIEW_MAP: Record<string, string> = {
      '/dashboard': 'dashboard',
      '/requests': 'requests',
      '/cases': 'cases',
      '/invoices': 'invoices',
      '/billing': 'billing',
      '/settings': 'settings',
      '/admin': 'admin',
    };

    const handlePopState = () => {
      const path = window.location.pathname;
      const view = PATH_VIEW_MAP[path];
      if (view) {
        setCurrentView(view);
        setSelectedCaseId(null);
        setSelectedCaseRecordId(null);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Check staging protection on app load
  useEffect(() => {
    if (stagingAccessGranted) {
      setStagingCheckDone(true);
      return;
    }
    apiClient
      .checkStagingAccess()
      .then(({ enabled }) => {
        setStagingProtectionEnabled(enabled);
        setStagingCheckDone(true);
      })
      .catch(() => {
        setStagingCheckDone(true); // Don't block app on failure
      });
  }, [stagingAccessGranted]);

  // Lock body scroll when in dashboard layout to prevent scrolling past content
  const isDashboardLayout = isAuthenticated && !isLoading && !isSpecialPage;
  useEffect(() => {
    if (isDashboardLayout) {
      const prevHtml = document.documentElement.style.overflow;
      const prevBody = document.body.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
      return () => {
        document.documentElement.style.overflow = prevHtml;
        document.body.style.overflow = prevBody;
      };
    }
  }, [isDashboardLayout]);

  // Handle view changes
  const handleViewChange = (view: string) => {
    setCurrentView(view);
    setSelectedCaseId(null);
    setSelectedCaseRecordId(null);
    setShowIntakeView(false); // Reset intake view when navigating normally
    setIsMobileMenuOpen(false); // Close mobile menu when navigating
  };

  // Handle case selection (from dashboard)
  const handleViewCase = (caseId: string) => {
    setSelectedCaseId(caseId);
    setCurrentView('case-detail');
    setShowIntakeView(false);
  };

  // Handle case record selection (from cases & records page)
  const handleViewCaseRecord = (caseRecordId: string) => {
    setSelectedCaseRecordId(caseRecordId);
    setCurrentView('case-record-detail');
    setShowIntakeView(false);
  };

  // Handle back to dashboard
  const handleBackToDashboard = () => {
    setCurrentView('dashboard');
    setSelectedCaseId(null);
    setShowIntakeView(false);
  };

  // Handle back to cases & records
  const handleBackToCasesAndRecords = () => {
    setCurrentView('cases');
    setSelectedCaseRecordId(null);
    setShowIntakeView(false);
  };

  // Handle intake view toggle
  const handleToggleIntakeView = () => {
    setShowIntakeView(!showIntakeView);
  };

  // Show system status if path is /system-status - check this FIRST
  if (showSystemStatus) {
    return (
      <>
        <Suspense fallback={<LoadingSpinner className="h-screen" message="Loading..." />}>
          <SystemStatus />
        </Suspense>
        <Toaster
          position="bottom-right"
          toastOptions={{
            style: {
              background: '#ffffff',
              border: '1px solid #e5e7eb',
              color: '#111827',
            },
          }}
        />
      </>
    );
  }

  // Show Terms page if path is /terms
  if (showTermsPage) {
    return (
      <>
        <Suspense fallback={<LoadingSpinner className="h-screen" message="Loading..." />}>
          <TermsAndConditions />
        </Suspense>
        <Toaster
          position="bottom-right"
          toastOptions={{
            style: {
              background: '#ffffff',
              border: '1px solid #e5e7eb',
              color: '#111827',
            },
          }}
        />
      </>
    );
  }

  // Show Privacy page if path is /privacy
  if (showPrivacyPage) {
    return (
      <>
        <Suspense fallback={<LoadingSpinner className="h-screen" message="Loading..." />}>
          <PrivacyPolicy />
        </Suspense>
        <Toaster
          position="bottom-right"
          toastOptions={{
            style: {
              background: '#ffffff',
              border: '1px solid #e5e7eb',
              color: '#111827',
            },
          }}
        />
      </>
    );
  }

  // Show SEO landing pages
  if (showPhysiciansPage) {
    return (
      <>
        <Suspense fallback={<LoadingSpinner className="h-screen" message="Loading..." />}>
          <PhysiciansPage />
        </Suspense>
        <Toaster
          position="bottom-right"
          toastOptions={{
            style: { background: '#ffffff', border: '1px solid #e5e7eb', color: '#111827' },
          }}
        />
      </>
    );
  }

  if (showComparePage) {
    return (
      <>
        <Suspense fallback={<LoadingSpinner className="h-screen" message="Loading..." />}>
          <ComparePage />
        </Suspense>
        <Toaster
          position="bottom-right"
          toastOptions={{
            style: { background: '#ffffff', border: '1px solid #e5e7eb', color: '#111827' },
          }}
        />
      </>
    );
  }

  if (showSideIncomePage) {
    return (
      <>
        <Suspense fallback={<LoadingSpinner className="h-screen" message="Loading..." />}>
          <SideIncomePage />
        </Suspense>
        <Toaster
          position="bottom-right"
          toastOptions={{
            style: { background: '#ffffff', border: '1px solid #e5e7eb', color: '#111827' },
          }}
        />
      </>
    );
  }

  if (showBlogPage) {
    return (
      <>
        <Suspense fallback={<LoadingSpinner className="h-screen" message="Loading..." />}>
          <BlogPage />
        </Suspense>
        <Toaster
          position="bottom-right"
          toastOptions={{
            style: { background: '#ffffff', border: '1px solid #e5e7eb', color: '#111827' },
          }}
        />
      </>
    );
  }

  if (showHowItWorksPage) {
    return (
      <>
        <Suspense fallback={<LoadingSpinner className="h-screen" message="Loading..." />}>
          <HowItWorksPage />
        </Suspense>
        <Toaster
          position="bottom-right"
          toastOptions={{
            style: { background: '#ffffff', border: '1px solid #e5e7eb', color: '#111827' },
          }}
        />
      </>
    );
  }

  if (showPricingPage) {
    return (
      <>
        <Suspense fallback={<LoadingSpinner className="h-screen" message="Loading..." />}>
          <PricingPage />
        </Suspense>
        <Toaster
          position="bottom-right"
          toastOptions={{
            style: { background: '#ffffff', border: '1px solid #e5e7eb', color: '#111827' },
          }}
        />
      </>
    );
  }

  if (showSecurityPage) {
    return (
      <>
        <Suspense fallback={<LoadingSpinner className="h-screen" message="Loading..." />}>
          <SecurityPage />
        </Suspense>
        <Toaster
          position="bottom-right"
          toastOptions={{
            style: { background: '#ffffff', border: '1px solid #e5e7eb', color: '#111827' },
          }}
        />
      </>
    );
  }

  if (showAttorneysPage) {
    return (
      <>
        <Suspense fallback={<LoadingSpinner className="h-screen" message="Loading..." />}>
          <AttorneysPage />
        </Suspense>
        <Toaster
          position="bottom-right"
          toastOptions={{
            style: { background: '#ffffff', border: '1px solid #e5e7eb', color: '#111827' },
          }}
        />
      </>
    );
  }

  // Show team member sign-up page (from invite link)
  if (showTeamSignUp && teamInviteId) {
    return (
      <>
        <Suspense fallback={<LoadingSpinner className="h-screen" message="Loading..." />}>
          <Auth
            defaultToSignUp
            signUpRole="TEAM_MEMBER"
            inviteId={teamInviteId}
            inviteEmail={teamInviteEmail}
            doctorName={teamInviteDoctorName}
          />
        </Suspense>
        <Toaster
          position="bottom-right"
          toastOptions={{
            style: { background: '#ffffff', border: '1px solid #e5e7eb', color: '#111827' },
          }}
        />
      </>
    );
  }

  // Show patient appointment scheduling page if route matches
  if (showAppointmentScheduling && appointmentId && appointmentToken) {
    return (
      <>
        <Suspense fallback={<LoadingSpinner className="h-screen" message="Loading..." />}>
          <AppointmentScheduling appointmentId={appointmentId} token={appointmentToken} />
        </Suspense>
        <Toaster
          position="bottom-right"
          toastOptions={{
            style: {
              background: '#ffffff',
              border: '1px solid #e5e7eb',
              color: '#111827',
            },
          }}
        />
      </>
    );
  }

  // Show doctor appointment management page if route matches
  if (showDoctorAppointment && appointmentId && appointmentToken) {
    return (
      <>
        <Suspense fallback={<LoadingSpinner className="h-screen" message="Loading..." />}>
          <DoctorAppointment appointmentId={appointmentId} token={appointmentToken} />
        </Suspense>
        <Toaster
          position="bottom-right"
          toastOptions={{
            style: {
              background: '#ffffff',
              border: '1px solid #e5e7eb',
              color: '#111827',
            },
          }}
        />
      </>
    );
  }

  // Show attorney dashboard if route matches
  if (showAttorneyDashboard && attorneyToken) {
    return (
      <>
        <Suspense fallback={<LoadingSpinner className="h-screen" message="Loading..." />}>
          <AttorneyDashboard token={attorneyToken} />
        </Suspense>
        <Toaster
          position="bottom-right"
          toastOptions={{
            style: {
              background: '#ffffff',
              border: '1px solid #e5e7eb',
              color: '#111827',
            },
          }}
        />
      </>
    );
  }

  // Show attorney client intake form (/client-intake/:vanityUrl)
  if (showAttorneyClientIntake && vanitySlug) {
    return (
      <>
        <Suspense fallback={<LoadingSpinner className="h-screen" message="Loading..." />}>
          <AttorneyClientIntake attorneyVanityUrl={vanitySlug} />
        </Suspense>
        <Toaster
          position="bottom-right"
          toastOptions={{
            style: {
              background: '#ffffff',
              border: '1px solid #e5e7eb',
              color: '#111827',
            },
          }}
        />
      </>
    );
  }

  // Show attorney intake if URL contains 'intake' OR if state is set OR if vanity URL detected
  const isIntakeView =
    showIntakeView ||
    window.location.search.includes('intake') ||
    /^\/intake(\/|$)/.test(window.location.pathname) ||
    vanitySlug;

  if (isIntakeView) {
    return (
      <>
        <Suspense fallback={<LoadingSpinner className="h-screen" message="Loading..." />}>
          <AttorneyIntake
            onBack={vanitySlug ? undefined : handleToggleIntakeView}
            doctorVanityUrl={vanitySlug || undefined}
          />
        </Suspense>
        <Toaster
          position="bottom-right"
          toastOptions={{
            style: {
              background: '#ffffff',
              border: '1px solid #e5e7eb',
              color: '#111827',
            },
          }}
        />
      </>
    );
  }

  // Show staging gate if protection is enabled and access not yet granted
  if (!stagingCheckDone) {
    return (
      <div className="h-screen bg-[#FAF7F0] flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-[#A66B32]/25 border-t-[#A66B32] rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (stagingProtectionEnabled && !stagingAccessGranted) {
    return <StagingGate onAccessGranted={() => setStagingAccessGranted(true)} />;
  }

  // Show loading state during authentication check
  if (isLoading) {
    return (
      <div className="h-screen bg-[#FAF7F0] flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-[#A66B32]/25 border-t-[#A66B32] rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Show Landing page when signed out */}
      <SignedOut>
        <Suspense fallback={<LoadingSpinner className="h-screen" message="Loading..." />}>
          {showAuthPage ? <Auth defaultToSignUp={authDefaultToSignUp} /> : <Landing />}
        </Suspense>
        <Toaster
          position="bottom-right"
          toastOptions={{
            style: {
              background: '#ffffff',
              border: '1px solid #e5e7eb',
              color: '#111827',
            },
          }}
        />
      </SignedOut>

      {/* Show Dashboard only when signed in */}
      <SignedIn>
        <SessionIdleGuard active={isAuthenticated && !isSpecialPage} />
        {/* OAuth return: wizard state in sessionStorage = user needs to finish steps 5-7 */}
        {(() => {
          const wizardState = sessionStorage.getItem('signup_wizard_state');
          if (wizardState) {
            try {
              const parsed = JSON.parse(wizardState);
              const savedAt = typeof parsed.savedAt === 'number' ? parsed.savedAt : 0;
              const age = Date.now() - savedAt;
              // Clear if no savedAt, savedAt in future, or older than 1 hour
              if (!savedAt || savedAt > Date.now() || age > 60 * 60 * 1000) {
                sessionStorage.removeItem('signup_wizard_state');
                return false;
              }
              return true;
            } catch {
              sessionStorage.removeItem('signup_wizard_state');
              return false;
            }
          }
          return false;
        })() ? (
          <Suspense fallback={<LoadingSpinner className="h-screen" message="Loading..." />}>
            <Auth defaultToSignUp oauthReturn />
          </Suspense>
        ) : user?.role === 'ATTORNEY' ? (
          // Attorney layout: simplified sidebar + attorney dashboard
          <>
            <Suspense fallback={<LoadingSpinner className="h-screen" message="Loading..." />}>
              <AttorneyLayout />
            </Suspense>
            <Toaster
              position="bottom-right"
              toastOptions={{
                style: {
                  background: '#ffffff',
                  border: '1px solid #e5e7eb',
                  color: '#111827',
                },
              }}
            />
          </>
        ) : user?.role === 'CLIENT' || !user?.role ? (
          // Incomplete profile — no wizard state, dead-end
          <div className="h-screen bg-[#FAF7F0] flex items-center justify-center">
            <div className="text-center max-w-md mx-auto p-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Profile Setup Incomplete</h2>
              <p className="text-gray-600 mb-4">
                Your account setup isn't complete. Please sign out and sign up again to finish
                setting up your profile.
              </p>
              <button
                type="button"
                onClick={() => (window.location.href = '/')}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Go to Home
              </button>
            </div>
          </div>
        ) : (
          // Doctor branch — wrapped in DoctorAccessGate so users without an
          // active subscription (post-grace) hit the paywall instead of the
          // dashboard shell.
          <DoctorAccessGate
            isDoctorContext={
              user?.role === 'DOCTOR' || user?.role === 'TEAM_MEMBER' || user?.role === 'ADMIN'
            }
          >
            <div className="h-screen bg-[#FAF7F0] flex overflow-hidden">
              {/* Desktop Sidebar - Hidden on mobile/tablet */}
              <div className="hidden lg:flex">
                <Sidebar currentView={currentView} onViewChange={handleViewChange} />
              </div>

              {/* Mobile Navigation Drawer */}
              {isMobileMenuOpen && (
                <div className="fixed inset-0 z-50 lg:hidden">
                  {/* Backdrop */}
                  {/* biome-ignore lint/a11y/useKeyWithClickEvents: pointer-only affordance; keyboard path provided by adjacent controls */}
                  {/* biome-ignore lint/a11y/noStaticElementInteractions: pointer-only affordance; keyboard path provided by adjacent controls */}
                  <div
                    className="absolute inset-0 bg-black/60 animate-in fade-in duration-200"
                    onClick={() => setIsMobileMenuOpen(false)}
                  />

                  {/* Drawer Panel */}
                  <div className="absolute inset-y-0 left-0 w-[85%] max-w-sm bg-white shadow-2xl animate-in slide-in-from-left duration-300 flex flex-col">
                    {/* Close Button */}
                    <button
                      type="button"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="absolute top-4 right-4 z-10 p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                      aria-label="Close menu"
                    >
                      <svg
                        aria-hidden="true"
                        className="w-6 h-6"
                        fill="none"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path d="M6 18L18 6M6 6l12 12"></path>
                      </svg>
                    </button>

                    {/* Sidebar Content */}
                    <div className="flex-1 overflow-y-auto">
                      <Sidebar
                        currentView={currentView}
                        onViewChange={(view) => {
                          handleViewChange(view);
                          setIsMobileMenuOpen(false);
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Main Content */}
              <div className="flex-1 flex flex-col overflow-hidden">
                <Header
                  onNavigateToSettings={() => setCurrentView('settings')}
                  onNavigateToRequests={() => setCurrentView('requests')}
                  onNavigateToRequest={(requestId) => {
                    setPendingRequestId(requestId);
                    setCurrentView('requests');
                  }}
                  onNavigateToInvoice={(invoiceId) => {
                    setPendingInvoiceId(invoiceId);
                    setCurrentView('invoices');
                  }}
                  onNavigateToCase={(caseId) => {
                    handleViewCase(caseId);
                  }}
                  onMobileMenuClick={() => setIsMobileMenuOpen(true)}
                  onNavigate={handleViewChange}
                />

                <main className="flex-1 overflow-auto bg-[#FAF7F0] min-h-0">
                  {currentView === 'dashboard' && (
                    <Dashboard
                      onViewCase={handleViewCase}
                      onNavigateToRequests={() => setCurrentView('requests')}
                      onNavigateToSettings={() => setCurrentView('settings')}
                      onNavigateToRequest={(requestId) => {
                        setPendingRequestId(requestId);
                        setCurrentView('requests');
                      }}
                      onNavigateToInvoice={(invoiceId) => {
                        setPendingInvoiceId(invoiceId);
                        setCurrentView('invoices');
                      }}
                      onNavigateToCase={(caseId) => {
                        handleViewCase(caseId);
                      }}
                    />
                  )}

                  {currentView === 'case-detail' && selectedCaseId && (
                    <Sentry.ErrorBoundary
                      fallback={({ error, resetError }) => (
                        <FeatureError
                          name="Case Details"
                          error={error instanceof Error ? error : undefined}
                          resetError={resetError}
                        />
                      )}
                    >
                      <Suspense
                        fallback={<LoadingSpinner className="h-full" message="Loading case..." />}
                      >
                        <AttorneyCaseDetail
                          caseId={selectedCaseId}
                          onBack={handleBackToDashboard}
                          useClerkAuth
                        />
                      </Suspense>
                    </Sentry.ErrorBoundary>
                  )}

                  {currentView === 'requests' && (
                    <Sentry.ErrorBoundary
                      fallback={({ error, resetError }) => (
                        <FeatureError
                          name="Requests"
                          error={error instanceof Error ? error : undefined}
                          resetError={resetError}
                        />
                      )}
                    >
                      <Suspense
                        fallback={
                          <LoadingSpinner className="h-full" message="Loading requests..." />
                        }
                      >
                        <PreScreeningRequests
                          initialRequestId={pendingRequestId}
                          onInitialRequestConsumed={() => setPendingRequestId(null)}
                        />
                      </Suspense>
                    </Sentry.ErrorBoundary>
                  )}

                  {currentView === 'cases' && !selectedCaseRecordId && (
                    <Sentry.ErrorBoundary
                      fallback={({ error, resetError }) => (
                        <FeatureError
                          name="Cases"
                          error={error instanceof Error ? error : undefined}
                          resetError={resetError}
                        />
                      )}
                    >
                      <Suspense
                        fallback={<LoadingSpinner className="h-full" message="Loading cases..." />}
                      >
                        <CasesAndRecords onViewCaseDetail={handleViewCaseRecord} />
                      </Suspense>
                    </Sentry.ErrorBoundary>
                  )}

                  {currentView === 'case-record-detail' && selectedCaseRecordId && (
                    <Sentry.ErrorBoundary
                      fallback={({ error, resetError }) => (
                        <FeatureError
                          name="Case Record"
                          error={error instanceof Error ? error : undefined}
                          resetError={resetError}
                        />
                      )}
                    >
                      <Suspense
                        fallback={
                          <LoadingSpinner className="h-full" message="Loading case details..." />
                        }
                      >
                        <CaseRecordDetail
                          caseId={selectedCaseRecordId}
                          onBack={handleBackToCasesAndRecords}
                        />
                      </Suspense>
                    </Sentry.ErrorBoundary>
                  )}

                  {currentView === 'invoices' && (
                    <Sentry.ErrorBoundary
                      fallback={({ error, resetError }) => (
                        <FeatureError
                          name="Invoices"
                          error={error instanceof Error ? error : undefined}
                          resetError={resetError}
                        />
                      )}
                    >
                      <Suspense
                        fallback={
                          <LoadingSpinner className="h-full" message="Loading invoices..." />
                        }
                      >
                        <Invoices
                          onNavigateToSettings={() => setCurrentView('settings')}
                          initialInvoiceId={pendingInvoiceId}
                          onInitialInvoiceConsumed={() => setPendingInvoiceId(null)}
                        />
                      </Suspense>
                    </Sentry.ErrorBoundary>
                  )}

                  {currentView === 'billing' && (
                    <Sentry.ErrorBoundary
                      fallback={({ error, resetError }) => (
                        <FeatureError
                          name="Billing"
                          error={error instanceof Error ? error : undefined}
                          resetError={resetError}
                        />
                      )}
                    >
                      <Suspense
                        fallback={
                          <LoadingSpinner className="h-full" message="Loading billing..." />
                        }
                      >
                        <Billing />
                      </Suspense>
                    </Sentry.ErrorBoundary>
                  )}

                  {currentView === 'settings' && (
                    <Sentry.ErrorBoundary
                      fallback={({ error, resetError }) => (
                        <FeatureError
                          name="Settings"
                          error={error instanceof Error ? error : undefined}
                          resetError={resetError}
                        />
                      )}
                    >
                      <Suspense
                        fallback={
                          <LoadingSpinner className="h-full" message="Loading settings..." />
                        }
                      >
                        <Settings />
                      </Suspense>
                    </Sentry.ErrorBoundary>
                  )}

                  {currentView === 'admin' && (
                    <Sentry.ErrorBoundary
                      fallback={({ error, resetError }) => (
                        <FeatureError
                          name="Admin"
                          error={error instanceof Error ? error : undefined}
                          resetError={resetError}
                        />
                      )}
                    >
                      <Suspense
                        fallback={<LoadingSpinner className="h-full" message="Loading admin..." />}
                      >
                        <AdminDiscountManagement />
                      </Suspense>
                    </Sentry.ErrorBoundary>
                  )}
                </main>
              </div>

              {/* Conflict Modal */}
              {selectedCaseId && (
                <ConflictModal
                  isOpen={isConflictModalOpen}
                  onClose={() => setIsConflictModalOpen(false)}
                  caseId={selectedCaseId}
                  plaintiff="Johnson vs Memorial Hospital"
                />
              )}

              {/* Toast Notifications */}
              <Toaster
                position="bottom-right"
                toastOptions={{
                  style: {
                    background: '#ffffff',
                    border: '1px solid #e5e7eb',
                    color: '#111827',
                  },
                }}
              />
            </div>
          </DoctorAccessGate>
        )}
      </SignedIn>
    </>
  );
}
